import { useMutation } from '@apollo/client';

import { UseErrorHandledMutation } from './types';
import useMutationErrorHandledWrapper from './useMutationErrorHandledWrapper';

/**
 * An adaption of useMutation which will handle certain errors without
 * any effort needed by the caller.
 *
 * The arguments are the same as useMutation but with some additional options.
 * @param mutation The mutation
 * @param options The same as useMutation's options with the following additions:
 * @param {boolean} [options.handleUnrecognizedErrors] Whether this should show a generic error message for
 *          otherwise unrecognized errors. Defaults to true.
 * @param {string} [options.unrecognizedErrorMessage] The message to display if this is the case.
 * Defaults to a generic message.
 * @returns The same signature as useMutation with some small additions.
 *      - The mutator in the index 0 position of the returned result will continue to return a
 *          Promise<ExecutionResult<TData>> for successful mutations. When an error occurs
 *          and it is one that has been handled, it will have a 'isHandledError' property added
 *          to it (the error will be an ApolloError object in this case). If the error was not
 *          handled, it will have not such additional property added to it (and may indeed be
 *          an object of any throwable type). The throwOnlyUnhandledErrors helper may be useful
 *          in responding here.
 *      - The results object in the index 1 position of the returned result will have
 *          the same signature as useMutation plus the following property
 *           - hasHandledError: When an error is contained in this results object,
 *               whether that error has already been handled by this hook. This can be
 *               used by the caller to ensure the UI is in the correct state. The value
 *               is undefined when there has not been an error, true if the current error
 *               is one that has been handled or false otherwise.
 */
const useErrorHandledMutation: UseErrorHandledMutation = (
  mutation,
  options = {},
) => {
  const {
    handleUnrecognizedErrors,
    unrecognizedErrorMessage,

    ...useMutationOptions
  } = options;
  const wrapperOptions = { handleUnrecognizedErrors, unrecognizedErrorMessage };
  return useMutationErrorHandledWrapper(
    useMutation(mutation, useMutationOptions),
    wrapperOptions,
  );
};

export default useErrorHandledMutation;
